import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { apiBaseUrl, prepareAuthHeaders } from "./apiBase";

export const usersApi = createApi({
  reducerPath: "usersApi",
  baseQuery: fetchBaseQuery({
    baseUrl: apiBaseUrl,
    prepareHeaders: (headers) =>
      prepareAuthHeaders(headers, { contentTypeJson: true }),
  }),
  tagTypes: ["User", "Company", "Employees", "CourierIntegration"],
  keepUnusedDataFor: 60,
  refetchOnMountOrArgChange: true,

  endpoints: (builder) => ({
    fetchUserData: builder.query({
      query: () => "authentication/user",
      transformResponse: (res) => (res?.success ? res.data : (res ?? {})),
      providesTags: [{ type: "User", id: "CURRENT" }],
    }),
    fetchCompanyUserData: builder.query({
      query: () => "company/user",
      transformResponse: (res) => (res?.success ? res.data : (res ?? {})),
      providesTags: [{ type: "Company", id: "USER" }],
    }),
    fetchCompanyUsers: builder.query({
      query: () => "company/company",
      transformResponse: (res) => (res?.success ? res.data : (res ?? [])),
      providesTags: (result) =>
        result?.length
          ? [
              ...result.map((u) => ({ type: "User", id: u.id })),
              { type: "Employees", id: "LIST" },
            ]
          : [{ type: "Employees", id: "LIST" }],
    }),
    fetchAbexIntegration: builder.query({
      query: () => "courier-integrations/abex",
      transformResponse: (res) =>
        res?.success
          ? res.data
          : { provider: "abex", isConfigured: false, isActive: false },
      providesTags: [{ type: "CourierIntegration", id: "ABEX" }],
    }),
    upsertAbexIntegration: builder.mutation({
      query: (data) => ({
        url: "courier-integrations/abex",
        method: "PUT",
        body: data,
      }),
      invalidatesTags: [
        { type: "CourierIntegration", id: "ABEX" },
        { type: "Company", id: "USER" },
      ],
    }),
    editCompanyData: builder.mutation({
      query: (data) => ({ url: "company/company", method: "PUT", body: data }),
      invalidatesTags: [
        { type: "Company", id: "USER" },
        { type: "Employees", id: "LIST" },
      ],
    }),
    updateProfile: builder.mutation({
      query: (data) => ({
        url: "authentication/profile",
        method: "PUT",
        body: data,
      }),
      invalidatesTags: [
        { type: "User", id: "CURRENT" },
        { type: "Company", id: "USER" },
      ],
    }),
    changePassword: builder.mutation({
      query: (body) => ({
        url: "authentication/change-password",
        method: "PUT",
        body,
      }),
    }),
  }),
});

export const {
  useFetchUserDataQuery,
  useFetchCompanyUserDataQuery,
  useFetchCompanyUsersQuery,
  useFetchAbexIntegrationQuery,
  useUpsertAbexIntegrationMutation,
  useEditCompanyDataMutation,
  useUpdateProfileMutation,
  useChangePasswordMutation,
} = usersApi;
